/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import * as z from 'zod';

/** A `Date` in JSON: `{ __desktopDate: <ISO string> }`, so ISO-looking strings stay strings. */
const taggedDateCodec = z.codec(z.strictObject({ __desktopDate: z.iso.datetime() }), z.date(), {
  decode: ({ __desktopDate }) => new Date(__desktopDate),
  encode: (date) => ({ __desktopDate: date.toISOString() }),
});

/**
 * JSON text ↔ value checked against `schema`. Dates anywhere in the value (form values have no
 * fixed shape) round-trip through `taggedDateCodec`.
 */
export const jsonCodec = <T extends z.ZodType>(schema: T) =>
  z.codec(z.string(), schema, {
    decode: (text, ctx) => {
      try {
        // `schema` validates the parsed value right after decoding.
        return JSON.parse(text, (_key, item) => {
          const date = taggedDateCodec.safeDecode(item);
          return date.success ? date.data : item;
        }) as z.input<T>;
      } catch (error) {
        ctx.issues.push({
          code: 'invalid_format',
          format: 'json',
          input: text,
          message: error instanceof Error ? error.message : 'Invalid JSON',
        });
        return z.NEVER;
      }
    },
    encode: (value) =>
      JSON.stringify(value, function (key, encoded) {
        const original = (this as Record<string, unknown>)[key];
        return original instanceof Date ? taggedDateCodec.encode(original) : encoded;
      }),
  });

const appStateJson = jsonCodec(z.unknown());

export const encodeAppState = (value: unknown): string => appStateJson.encode(value);

export const decodeAppState = <T>(value: string): T => appStateJson.decode(value) as T;

/** Reads a stored value; `undefined` when missing, unreadable or corrupt. */
export function readStoredValue<T>(key: string): T | undefined {
  try {
    const value = localStorage.getItem(key);
    if (value === null) return undefined;
    const decoded = appStateJson.safeDecode(value);
    return decoded.success ? (decoded.data as T) : undefined;
  } catch {
    return undefined;
  }
}

/** Writes a value; returns false when storage is unavailable or full. */
export function writeStoredValue(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, encodeAppState(value));
    return true;
  } catch {
    return false;
  }
}

export function removeStoredValues(prefix: string) {
  try {
    const keys = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index));
    for (const key of keys) {
      if (key?.startsWith(prefix)) localStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable: nothing was stored.
  }
}
